import { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

function makeNoiseBuffer(context) {
  const length = context.sampleRate * 4;
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let index = 0; index < length; index += 1) {
    const white = (Math.random() * 2) - 1;
    last = (last + (0.025 * white)) / 1.025;
    data[index] = last * 3.4;
  }
  return buffer;
}

function startSoundscape(audioRef) {
  if (audioRef.current) return audioRef.current;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;

  const context = new AudioContext();
  const master = context.createGain();
  master.gain.setValueAtTime(0.0001, context.currentTime);
  master.gain.exponentialRampToValueAtTime(0.13, context.currentTime + 1.2);
  master.connect(context.destination);

  const ocean = context.createBufferSource();
  ocean.buffer = makeNoiseBuffer(context);
  ocean.loop = true;
  const oceanFilter = context.createBiquadFilter();
  oceanFilter.type = 'lowpass';
  oceanFilter.frequency.value = 470;
  oceanFilter.Q.value = 0.7;
  const oceanGain = context.createGain();
  oceanGain.gain.value = 0.065;
  ocean.connect(oceanFilter).connect(oceanGain).connect(master);
  ocean.start();

  const droneGain = context.createGain();
  droneGain.gain.value = 0.045;
  droneGain.connect(master);
  const oscillators = [73.42, 110, 146.83].map((frequency, index) => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = index === 0 ? 'sine' : 'triangle';
    oscillator.frequency.value = frequency;
    oscillator.detune.value = index === 1 ? -7 : index === 2 ? 6 : 0;
    gain.gain.value = index === 0 ? 0.6 : 0.22;
    oscillator.connect(gain).connect(droneGain);
    oscillator.start();
    return oscillator;
  });

  const lfo = context.createOscillator();
  const lfoGain = context.createGain();
  lfo.frequency.value = 0.085;
  lfoGain.gain.value = 130;
  lfo.connect(lfoGain).connect(oceanFilter.frequency);
  lfo.start();

  const strikeDrum = () => {
    if (context.state === 'closed') return;
    const now = context.currentTime;
    const drum = context.createOscillator();
    const drumGain = context.createGain();
    drum.type = 'sine';
    drum.frequency.setValueAtTime(92, now);
    drum.frequency.exponentialRampToValueAtTime(43, now + 0.42);
    drumGain.gain.setValueAtTime(0.0001, now);
    drumGain.gain.exponentialRampToValueAtTime(0.055, now + 0.018);
    drumGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
    drum.connect(drumGain).connect(master);
    drum.start(now);
    drum.stop(now + 0.38);
  };

  const playTreasureChime = () => {
    if (context.state === 'closed') return;
    [659.25, 830.61, 987.77].forEach((frequency, index) => {
      const now = context.currentTime + (index * 0.11);
      const bell = context.createOscillator();
      const bellGain = context.createGain();
      bell.type = 'sine';
      bell.frequency.value = frequency;
      bellGain.gain.setValueAtTime(0.0001, now);
      bellGain.gain.exponentialRampToValueAtTime(0.032, now + 0.015);
      bellGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.65);
      bell.connect(bellGain).connect(master);
      bell.start(now);
      bell.stop(now + 0.7);
    });
  };

  const reelPulse = () => {
    if (context.state === 'closed') return;
    const now = context.currentTime;
    [0, 0.12, 0.24, 0.36].forEach((offset) => {
      const tick = context.createOscillator();
      const tickGain = context.createGain();
      tick.type = 'triangle';
      tick.frequency.value = 740 + Math.random() * 120;
      tickGain.gain.setValueAtTime(0.0001, now + offset);
      tickGain.gain.exponentialRampToValueAtTime(0.018, now + offset + 0.01);
      tickGain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.08);
      tick.connect(tickGain).connect(master);
      tick.start(now + offset);
      tick.stop(now + offset + 0.1);
    });
  };

  const playCasinoGroove = () => {
    if (context.state === 'closed') return;
    const base = context.currentTime;
    const leadNotes = [293.66, 369.99, 440, 554.37, 493.88, 369.99, 329.63, 440];
    leadNotes.forEach((frequency, index) => {
      const now = base + (index * 0.145);
      const tone = context.createOscillator();
      const toneGain = context.createGain();
      const toneFilter = context.createBiquadFilter();
      tone.type = index % 2 ? 'triangle' : 'sine';
      tone.frequency.setValueAtTime(frequency, now);
      toneFilter.type = 'lowpass';
      toneFilter.frequency.setValueAtTime(1250, now);
      toneGain.gain.setValueAtTime(0.0001, now);
      toneGain.gain.exponentialRampToValueAtTime(0.017, now + 0.012);
      toneGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.19);
      tone.connect(toneFilter).connect(toneGain).connect(master);
      tone.start(now);
      tone.stop(now + 0.22);
    });

    [0, 0.42, 0.84].forEach((offset) => {
      const now = base + offset;
      const hat = context.createOscillator();
      const hatGain = context.createGain();
      hat.type = 'square';
      hat.frequency.setValueAtTime(2200 + Math.random() * 420, now);
      hatGain.gain.setValueAtTime(0.0001, now);
      hatGain.gain.exponentialRampToValueAtTime(0.006, now + 0.006);
      hatGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
      hat.connect(hatGain).connect(master);
      hat.start(now);
      hat.stop(now + 0.05);
    });

    const bass = context.createOscillator();
    const bassGain = context.createGain();
    bass.type = 'sine';
    bass.frequency.setValueAtTime(73.42, base);
    bassGain.gain.setValueAtTime(0.0001, base);
    bassGain.gain.exponentialRampToValueAtTime(0.026, base + 0.03);
    bassGain.gain.exponentialRampToValueAtTime(0.0001, base + 0.38);
    bass.connect(bassGain).connect(master);
    bass.start(base);
    bass.stop(base + 0.42);
  };

  reelPulse();
  playCasinoGroove();
  const drumTimer = window.setInterval(strikeDrum, 9200);
  const chimeTimer = window.setInterval(playTreasureChime, 22000);
  const reelTimer = window.setInterval(reelPulse, 2900);
  const grooveTimer = window.setInterval(playCasinoGroove, 1320);
  const soundscape = {
    context,
    master,
    ocean,
    oscillators,
    lfo,
    drumTimer,
    chimeTimer,
    reelTimer,
    grooveTimer,
    playTreasureChime,
  };
  audioRef.current = soundscape;
  return soundscape;
}

function stopSoundscape(audioRef) {
  const soundscape = audioRef.current;
  if (!soundscape) return;
  window.clearInterval(soundscape.drumTimer);
  window.clearInterval(soundscape.chimeTimer);
  window.clearInterval(soundscape.reelTimer);
  window.clearInterval(soundscape.grooveTimer);
  const now = soundscape.context.currentTime;
  soundscape.master.gain.cancelScheduledValues(now);
  soundscape.master.gain.setValueAtTime(Math.max(soundscape.master.gain.value, 0.0001), now);
  soundscape.master.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
  window.setTimeout(() => soundscape.context.close().catch(() => {}), 300);
  audioRef.current = null;
}

function persistSoundState(isOn) {
  try { localStorage.setItem('vl_pirate_sound', isOn ? 'on' : 'off'); } catch {}
}

export default function PirateLobbyExperience() {
  const audioRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  const toggleSound = useCallback(() => {
    if (soundOn) {
      stopSoundscape(audioRef);
      setSoundOn(false);
      persistSoundState(false);
      return;
    }
    const soundscape = startSoundscape(audioRef);
    soundscape?.context.resume().catch(() => {});
    const isPlaying = Boolean(soundscape);
    setSoundOn(isPlaying);
    persistSoundState(isPlaying);
  }, [soundOn]);

  useEffect(() => () => {
    stopSoundscape(audioRef);
  }, []);

  return (
    <button
      type="button"
      className={`vl-sound-dial${soundOn ? ' is-playing' : ''}`}
      onClick={toggleSound}
      aria-label={soundOn ? 'Mute pirate soundtrack' : 'Play pirate soundtrack'}
      data-testid="pirate-sound-toggle"
    >
      {soundOn ? <Volume2 /> : <VolumeX />}
      <span>{soundOn ? 'SOUND ON' : 'SOUND OFF'}</span>
    </button>
  );
}
